import {
  ApplicationRef,
  createComponent,
  DOCUMENT,
  EmbeddedViewRef,
  EnvironmentInjector,
  inject,
  Injectable,
} from '@angular/core';
import { SnackbarComponent } from './snackbar.component';
import type { SnackbarType } from './snackbar.model';

export interface NotificationOptions {
  type: SnackbarType;
  duration?: number;
}

@Injectable({
  providedIn: 'root',
})
export class NotificationService {
  private appRef = inject(ApplicationRef);
  private environmentInjector = inject(EnvironmentInjector);
  private document = inject(DOCUMENT);
  private notificationContainer: HTMLElement | null = null;

  show(
    message: string,
    options: NotificationOptions = { type: 'success', duration: 3000 },
  ) {
    if (!this.notificationContainer) {
      this.notificationContainer = this.document.createElement('div');
      this.notificationContainer.classList.add('snackbar-container');
      this.notificationContainer.setAttribute('popover', 'manual');
      this.document.body.appendChild(this.notificationContainer);
    }
    this.showContainerAboveOpenPopovers(this.notificationContainer);

    const componentRef = createComponent(SnackbarComponent, {
      environmentInjector: this.environmentInjector,
    });

    componentRef.setInput('message', message);
    componentRef.setInput('type', options.type);

    this.appRef.attachView(componentRef.hostView);

    const domElem = (componentRef.hostView as EmbeddedViewRef<any>)
      .rootNodes[0] as HTMLElement;
    this.notificationContainer.appendChild(domElem);

    const sub = componentRef.instance.dismissed.subscribe(() => {
      componentRef.destroy();
      sub.unsubscribe();
    });

    setTimeout(() => componentRef.instance.dismiss(), options.duration ?? 3000);
  }

  showSuccess(
    message: string,
    options: NotificationOptions = { type: 'success', duration: 3000 },
  ) {
    this.show(message, { ...options, type: 'success' });
  }

  showError(
    message: string,
    options: NotificationOptions = { type: 'error', duration: 3000 },
  ) {
    this.show(message, { ...options, type: 'error' });
  }

  showWarning(
    message: string,
    options: NotificationOptions = { type: 'warning', duration: 3000 },
  ) {
    this.show(message, { ...options, type: 'warning' });
  }

  private showContainerAboveOpenPopovers(container: HTMLElement): void {
    if (container.matches(':popover-open')) {
      container.hidePopover();
    }
    container.showPopover();
  }
}
